import React, { useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { datePickerLocale } from '../../../../components/common/datepicker-locale';
import { getEmployeeWorkingHours } from '../../../../features/timesheet/timesheet.slice';

/* Portal the react-datepicker popper out of the DOM tree so it can't be
   clipped by an ancestor's overflow:hidden / transform / containing-block.
   Renders directly under <body> via React's createPortal. */
const WhPopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  ReactDOM.createPortal(children, document.body);

/* Bridge between the local `yyyy-mm-dd` string state (consumed by the
   Redux `getEmployeeWorkingHours` thunk and the appliedDateRange compare)
   and react-datepicker's `Date | null` model. */
const parseIso = (s?: string): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const toIso = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface CalendarSummaryData {
    serialNo: string;
    invoiceNo: string;
    userId: string;
    absences: string;
    hours: string;
    issuedDate: string;
}

const WorkedHours: React.FC = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch<any>();
    
    // Get employee working hours data from Redux
    const { employeeWorkingHours, employeeWorkingHoursLoading } = useSelector(
        (state: any) => state.timesheet || { employeeWorkingHours: [], employeeWorkingHoursLoading: false }
    );
    
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [appliedDateRange, setAppliedDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
    const [currentPage, setCurrentPage] = useState<number>(1);

    const itemsPerPage = 10;
    
    // Ensure employeeWorkingHours is always an array and map to table format
    const workingHoursData = useMemo(() => {
        // Handle both array and object with workingHours property
        let rawData = [];
        if (Array.isArray(employeeWorkingHours)) {
            rawData = employeeWorkingHours;
        } else if (employeeWorkingHours?.workingHours && Array.isArray(employeeWorkingHours.workingHours)) {
            rawData = employeeWorkingHours.workingHours;
        }
        
        console.log('Raw working hours data:', rawData);
        
        return rawData.map((item: any) => ({
            serialNo: item.id?.toString() || 'N/A',
            invoiceNo: item.invoiceNumber?.toString() || 'N/A',
            userId: item.userId?.toString() || 'N/A',
            absences: 'N/A',
            hours: item.workingHour ? `${item.workingHour}h` : 'N/A',
            issuedDate: 'N/A'
        }));
    }, [employeeWorkingHours]);
    
    const totalPages = Math.ceil(workingHoursData.length / itemsPerPage);
    
    const currentPageData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return workingHoursData.slice(startIndex, endIndex);
    }, [currentPage, workingHoursData]);

    // Auto-call API on component mount
    useEffect(() => {
        console.log('Fetching employee working hours on component mount');
        dispatch(
            getEmployeeWorkingHours({
                fromDate: startDate || '',
                toDate: endDate || '',
            })
        );
        setAppliedDateRange({ start: startDate || '', end: endDate || '' });
    }, []);

    // Auto-call API when both dates are filled
    useEffect(() => {
        if (startDate && endDate) {
            console.log('Fetching employee working hours from', startDate, 'to', endDate);
            dispatch(
                getEmployeeWorkingHours({
                    fromDate: startDate,
                    toDate: endDate,
                })
            );
            setAppliedDateRange({ start: startDate, end: endDate });
        }
    }, [startDate, endDate, dispatch]);

    const handleLoadData = () => {
        if (startDate && endDate) {
            console.log('Fetching employee working hours from', startDate, 'to', endDate);
            dispatch(
                getEmployeeWorkingHours({
                    fromDate: startDate,
                    toDate: endDate,
                })
            );
            setAppliedDateRange({ start: startDate, end: endDate });
        }
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const getPaginationNumbers = () => {
        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        
        if (currentPage <= 2) {
            return [1, 2, '...', totalPages];
        }
        
        if (currentPage >= totalPages - 1) {
            return [1, '...', totalPages - 1, totalPages];
        }
        
        return [1, '...', currentPage, '...', totalPages];
    };

    return (
        <div className="worked-hours">
            <div className="worked-hours-header">
                <div className="date-filter-group">
                    <div className="input-field">
                        <label className="input-label">{t('FROMS')}</label>
                        <div className="input-box">
                            {/* Swapped native <input type="date"> for react-datepicker
                                so the displayed format is `yyyy-mm-dd` in every
                                browser. Local `startDate` state stays as the same
                                `yyyy-mm-dd` ISO string the Redux thunk consumes. */}
                            <DatePicker
                                selected={parseIso(startDate)}
                                onChange={(d: Date | null) => setStartDate(toIso(d))}
                                dateFormat="yyyy-MM-dd"
                                placeholderText="yyyy-mm-dd"
                                locale={datePickerLocale(i18n.language)}
                                className="date-input"
                                wrapperClassName="wh-dp"
                                popperContainer={WhPopperPortal}
                                popperProps={{ strategy: 'fixed' }}
                                popperClassName="wh-dp-popper"
                                calendarClassName="wh-dp-calendar"
                                onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="scroll"
                                autoComplete="off"
                            />
                            <svg className="calendar-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.8333 3.33334H4.16667C3.24619 3.33334 2.5 4.07953 2.5 5.00001V16.6667C2.5 17.5872 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5872 17.5 16.6667V5.00001C17.5 4.07953 16.7538 3.33334 15.8333 3.33334Z" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M13.3333 1.66666V4.99999" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M6.66667 1.66666V4.99999" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M2.5 8.33334H17.5" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                    </div>

                    <div className="input-field">
                        <label className="input-label">{t('TO')}</label>
                        <div className="input-box">
                            {/* Same picker as From — `minDate` blocks selecting
                                an end date earlier than the start. */}
                            <DatePicker
                                selected={parseIso(endDate)}
                                onChange={(d: Date | null) => setEndDate(toIso(d))}
                                dateFormat="yyyy-MM-dd"
                                placeholderText="yyyy-mm-dd"
                                locale={datePickerLocale(i18n.language)}
                                minDate={parseIso(startDate) ?? undefined}
                                className="date-input"
                                wrapperClassName="wh-dp"
                                popperContainer={WhPopperPortal}
                                popperProps={{ strategy: 'fixed' }}
                                popperClassName="wh-dp-popper"
                                calendarClassName="wh-dp-calendar"
                                onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="scroll"
                                autoComplete="off"
                            />
                            <svg className="calendar-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.8333 3.33334H4.16667C3.24619 3.33334 2.5 4.07953 2.5 5.00001V16.6667C2.5 17.5872 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5872 17.5 16.6667V5.00001C17.5 4.07953 16.7538 3.33334 15.8333 3.33334Z" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M13.3333 1.66666V4.99999" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M6.66667 1.66666V4.99999" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M2.5 8.33334H17.5" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </div>
                
                <button className="load-button" onClick={handleLoadData}>
                    {t('LOAD')}
                </button>
            </div>

            <div className="monthly-calendar-summary">
                <div className="calendar-card">
                    <div className="calendar-card-header">
                        <h5 className="calendar-title">{t('MONTHLY_CALENDAR_SUMMARY')}</h5>
                    </div>

                    <div className="calendar-card-body">
                        <div className="calendar-table-wrapper">
                            <div className="calendar-table-container">
                                <div className="calendar-table">
                                <div className="calendar-table-row calendar-table-header">
                                    <div className="calendar-col calendar-col-serial">{t('SERIAL_NO')}</div>
                                    <div className="calendar-col calendar-col-invoice">{t('INVOICE_NO') || 'Invoice No.'}</div>
                                    <div className="calendar-col calendar-col-userid">{t('USER_ID')}</div>
                                    <div className="calendar-col calendar-col-absences">{t('ABSENCES')}</div>
                                    <div className="calendar-col calendar-col-hours">{t('HOURSS')}</div>
                                    <div className="calendar-col calendar-col-issued">{t('ISSUED_DATE')}</div>
                                    </div>

                                <div className="calendar-table-body">
                                    {employeeWorkingHoursLoading ? (
                                        Array.from({ length: 8 }).map((_, i) => (
                                            <div key={`sk-worked-${i}`} className="calendar-table-row calendar-table-data-row worked-skeleton-row">
                                                <div className="calendar-col calendar-col-serial"><span className="sk sk-line" /></div>
                                                <div className="calendar-col calendar-col-invoice"><span className="sk sk-line" /></div>
                                                <div className="calendar-col calendar-col-userid"><span className="sk sk-line" /></div>
                                                <div className="calendar-col calendar-col-absences"><span className="sk sk-line" /></div>
                                                <div className="calendar-col calendar-col-hours"><span className="sk sk-line" /></div>
                                                <div className="calendar-col calendar-col-issued"><span className="sk sk-line" /></div>
                                            </div>
                                        ))
                                    ) : currentPageData.length > 0 ? (
                                        currentPageData.map((item: CalendarSummaryData, index: number) => (
                                            <div key={`${item.serialNo}-${index}`} className="calendar-table-row calendar-table-data-row">
                                                <div className="calendar-col calendar-col-serial">
                                                    <span className="serial-number">{item.serialNo}</span>
                                                </div>
                                                <div className="calendar-col calendar-col-invoice">{item.invoiceNo}</div>
                                                <div className="calendar-col calendar-col-userid">{item.userId}</div>
                                                <div className="calendar-col calendar-col-absences">{item.absences}</div>
                                                <div className="calendar-col calendar-col-hours">{item.hours}</div>
                                                <div className="calendar-col calendar-col-issued">{item.issuedDate}</div>
                                               
                                            </div>
                                        ))
                                    ) : (
                                        <div className="calendar-table-row calendar-table-data-row">
                                            <div className="calendar-col" style={{ 
                                                gridColumn: '1 / -1', 
                                                display: 'flex', 
                                                justifyContent: 'center', 
                                                alignItems: 'center',
                                                padding: '40px 20px',
                                                color: '#68727D',
                                                fontSize: '15px'
                                            }}>
                                                {t('NO_DATA_AVAILABLE')}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                       
                        </div>
                    </div>
                     <div className="calendar-pagination">
                                <button 
                                    className="pagination-btn pagination-prev"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span>{t('PREV')}</span>
                                </button>

                                <div className="pagination-numbers">
                                    {getPaginationNumbers().map((pageNum, idx) => (
                                        typeof pageNum === 'number' ? (
                                            <button
                                                key={`page-${pageNum}-${idx}`}
                                                className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                                                onClick={() => handlePageChange(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        ) : (
                                            <span key={`ellipsis-${idx}`} className="pagination-ellipsis">
                                                {pageNum}
                                            </span>
                                        )
                                    ))}
                                </div>

                                <button 
                                    className="pagination-btn pagination-next"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    <span>{t('NEXT')}</span>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            </div>
                </div>
            </div>
        </div>
    );
};

export default WorkedHours;
